/**
 * MiseEnRayonTab — Onglet "Mise en Rayon" de la page Réception POS
 *
 * Extrait de BackroomManagement.tsx, adapté pour le contexte POS (touch-friendly).
 * Affiche les lots en réserve, permet le transfert vers le rayon,
 * la capture de photos d'étiquettes et l'impression d'étiquettes Zebra.
 */
import { useState, useEffect, useRef } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { posApi } from '../../api/pos.api';
import { usePOSStore } from '../../hooks/usePOSStore';
import { api } from '../../services/api';
import { BackroomProduct, ProductLot } from '../../types/lots.types';
import { formatQty } from '../../utils/pos.utils';
import { Product } from '../../types/pos.types';
import {
  Package, Calendar, User, Factory, BadgeCheck,
  AlertCircle, Edit, FileText, Image, Camera, Printer,
} from 'lucide-react';
import ProductEditModal from '../ProductEditModal';
import WebcamCaptureModal from '../ui/WebcamCaptureModal';
import BarcodeScannerModal from '../ui/BarcodeScannerModal';
import DLCInput from '../ui/DLCInput';
import { PrintShelfLabelsModal } from '../labels/PrintShelfLabelsModal';

interface MiseEnRayonTabProps {
  scannedBarcode: string | null;
  onBarcodeClear: () => void;
  onBackroomCountChange: (count: number) => void;
  /** Recherche pré-remplie à l'ouverture (arrivée depuis une alerte de réappro). */
  initialSearch?: string;
}

export default function MiseEnRayonTab({
  scannedBarcode,
  onBarcodeClear,
  onBackroomCountChange,
  initialSearch = '',
}: MiseEnRayonTabProps) {
  const { tenant } = useTenant();
  const [products, setProducts] = useState<BackroomProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [movingLot, setMovingLot] = useState<string | null>(null);
  // Annonces posées dans cette session : la liste des lots ne les relit pas
  // (elle ne sert que la réserve), et le retour visuel doit être immédiat.
  // Le vendeur de la session POS : c'est lui qui range, pas le compte du personnel
  // sous lequel tourne le navigateur.
  const { vendorId } = usePOSStore();
  /**
   * Décompte de la semaine, affiché en gris au-dessus de la liste.
   *
   * Il COMPTE, il ne réclame rien : pas de bouton, pas d'alerte, aucune action.
   * C'est le seul rappel qu'on se donne — « douze articles sont arrivés en
   * rayon, trois ont été annoncés » — et il doit rester silencieux les semaines
   * où personne n'annonce. Une ligne qui insiste finit par être cliquée pour
   * qu'elle se taise, et on annonce alors n'importe quoi.
   */
  const [decompteSemaine, setDecompteSemaine] = useState<{ total: number; annonces: number } | null>(
    null,
  );

  const [produitsAnnonces, setProduitsAnnonces] = useState<Set<string>>(new Set());
  const [annonceEnCours, setAnnonceEnCours] = useState<string | null>(null);
  const [moveQuantities, setMoveQuantities] = useState<Record<string, number>>({});
  // Buffer texte de saisie par lot — indispensable pour accepter la virgule (poids) sur iOS
  const [moveQuantityInputs, setMoveQuantityInputs] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState(initialSearch);

  // Modal d'édition de produit
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Capture photo étiquette
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamLotId, setWebcamLotId] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState<string | null>(null);

  // Preview photo
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);

  // Impression étiquette
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelProductId, setLabelProductId] = useState<string | null>(null);
  const [labelLotId, setLabelLotId] = useState<string | null>(null);

  // Scan code-barres caméra
  const [scannerOpen, setScannerOpen] = useState(false);

  // Filtres Rayon (catégorie niveau 0) et Fournisseur
  const [selectedRayon, setSelectedRayon] = useState<string | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);

  // Ref pour le scroll auto
  const productRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [highlightedProductId, setHighlightedProductId] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api
      .get<Array<{ deja_annonce: boolean }>>('/products/novelty-candidates?jours=7')
      .then((liste) => {
        if (!vivant || !Array.isArray(liste)) return;
        setDecompteSemaine({
          total: liste.length,
          annonces: liste.filter((c) => c.deja_annonce).length,
        });
      })
      // Silencieux : ce décompte est un confort, il ne doit jamais empêcher de ranger.
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, []);

  useEffect(() => {
    if (tenant?.id) {
      loadBackroomLots();
    }
  }, [tenant?.id]);

  // Barcode scan-to-highlight
  useEffect(() => {
    if (!scannedBarcode || products.length === 0) return;

    const match = products.find(pg =>
      pg.product.barcode === scannedBarcode ||
      pg.product.code === scannedBarcode
    );

    if (match) {
      setSearchTerm(scannedBarcode);
      setExpandedProducts(prev => new Set(prev).add(match.product.id));
      setHighlightedProductId(match.product.id);

      setTimeout(() => {
        productRefs.current[match.product.id]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);

      setTimeout(() => setHighlightedProductId(null), 3000);
    }

    onBarcodeClear();
  }, [scannedBarcode]);

  /**
   * Recharge la liste des lots en réserve.
   *
   * `silencieux` : ne pas afficher l'écran de chargement. Il remplace TOUTE la
   * liste, donc le navigateur perd la position de défilement et l'opérateur est
   * renvoyé en haut après chaque mise en rayon — insupportable quand on range
   * une livraison de trente lots. Sur un rafraîchissement qui suit une action,
   * la liste reste affichée et se met à jour en place.
   */
  const loadBackroomLots = async (options: { silencieux?: boolean } = {}) => {
    try {
      if (!options.silencieux) setLoading(true);
      const data = await posApi.getBackroomLots();
      setProducts(data);
      onBackroomCountChange(data.length);

      // Default qty = 0 (force l'utilisateur à saisir explicitement la quantité à mettre en rayon).
      // Voir bug du 01/06/2026 : default=stock_backroom + cap silencieux à max → l'opérateur tape "6"
      // sur un champ contenant "12" → résultat "126" tronqué à 12 → tout le stock part en rayon par erreur.
      const initialQuantities: Record<string, number> = {};
      const initialInputs: Record<string, string> = {};
      data.forEach((productGroup: BackroomProduct) => {
        productGroup.lots.forEach((lot: ProductLot) => {
          initialQuantities[lot.id] = 0;
          initialInputs[lot.id] = '0';
        });
      });
      setMoveQuantities(initialQuantities);
      setMoveQuantityInputs(initialInputs);
    } catch (err: any) {
      console.error('Failed to load backroom lots:', err);
    } finally {
      if (!options.silencieux) setLoading(false);
    }
  };

  // Transfert réserve → rayon.
  //
  // Note (05/08/2026) : la mise en rayon ne demande PLUS « au poids ou à la pièce ? »
  // et n'envoie plus `is_weighted`. L'unité de vente est déjà décidée en amont — la
  // ligne de livraison la porte, la réception la pose sur le lot ET sur la fiche —,
  // donc ce transfert est un simple mouvement de stock, comme tous les autres appels
  // à moveToDisplay (fiche produit, réserve, résolution hors-ligne).
  // La confirmation écrasait lot + fiche à chaque transfert : un tap sur le mauvais
  // bouton a fait passer les olives Kalamata (vendues au kg, ligne de livraison en kg)
  // à la pièce. L'unité se corrige sur la fiche produit, là où elle appartient.
  // Sélection multiple : le pain arrive par vingt lots, les envoyer un par un
  // était le geste le plus répétitif de la réception (demande du 04/09/2026).
  const [lotsSelectionnes, setLotsSelectionnes] = useState<Set<string>>(new Set());
  const [envoiEnLot, setEnvoiEnLot] = useState<{ fait: number; total: number } | null>(null);

  /** Coche/décoche tous les lots en réserve d'un produit — c'est le bloc qu'on
   *  voit dans la liste, les lots ne s'ouvrent qu'au dépliage. */
  const basculerSelectionProduit = (lots: Array<{ id: string; stock_backroom: number }>) => {
    const ids = lots.filter(l => l.stock_backroom > 0).map(l => l.id);
    if (ids.length === 0) return;
    setLotsSelectionnes(prev => {
      const s = new Set(prev);
      const toutCoche = ids.every(id => s.has(id));
      for (const id of ids) {
        if (toutCoche) s.delete(id);
        else s.add(id);
      }
      return s;
    });
  };

  const basculerSelection = (lotId: string) => {
    setLotsSelectionnes(prev => {
      const s = new Set(prev);
      if (s.has(lotId)) s.delete(lotId);
      else s.add(lotId);
      return s;
    });
  };

  const handleMoveToDisplay = async (lotId: string, quantity: number) => {
    if (quantity <= 0) return;

    try {
      setMovingLot(lotId);
      const result = await posApi.moveToDisplay(lotId, quantity);
      loadBackroomLots({ silencieux: true });

      // Note (01/06/2026) : on n'ouvre PLUS automatiquement ProductEditModal après le transfert.
      // Cela évitait les corrections manuelles qui écrasaient le stock du lot (cf. Bug #2 Mojito).
      // L'opérateur peut toujours éditer le produit via le bouton crayon de la carte.
      if (result.productActivated) {
        alert(`${formatQty(quantity)} en rayon.\nProduit activé !`);
      }
      // Sinon : feedback silencieux (la carte disparaît du backroom + indicateur "en rayon").
    } catch (err: any) {
      alert(err.message || 'Échec du déplacement vers le rayon');
    } finally {
      setMovingLot(null);
    }
  };

  // Mettre TOUT ce lot en rayon (1 tap) : remplit la quantité = stock réserve du lot,
  // puis ouvre le choix pesé/pièce. Évite de saisir la quantité à la main lot par lot.
  // Scission d'un lot : une livraison arrive parfois sous un seul numéro alors
  // que les colis portent DEUX dates. Le modèle impose une DLC par lot.
  const [splitLot, setSplitLot] = useState<ProductLot | null>(null);
  const [splitQtyBackroom, setSplitQtyBackroom] = useState('');
  const [splitQtyDisplay, setSplitQtyDisplay] = useState('');
  const [splitDlc, setSplitDlc] = useState('');
  const [splitting, setSplitting] = useState(false);

  const openSplit = (lot: ProductLot) => {
    setSplitLot(lot);
    setSplitQtyBackroom('');
    setSplitQtyDisplay('');
    setSplitDlc('');
  };

  const confirmSplit = async () => {
    if (!splitLot) return;
    const qb = parseFloat((splitQtyBackroom || '0').replace(',', '.')) || 0;
    const qd = parseFloat((splitQtyDisplay || '0').replace(',', '.')) || 0;
    if (qb <= 0 && qd <= 0) {
      alert('Indiquez la quantité qui porte l\'autre DLC.');
      return;
    }
    if (!splitDlc) {
      alert('Indiquez la DLC du nouveau lot.');
      return;
    }
    try {
      setSplitting(true);
      const r = await posApi.splitLot(splitLot.id, {
        quantity_backroom: qb || undefined,
        quantity_display: qd || undefined,
        expiry_date: splitDlc,
      });
      setSplitLot(null);
      await loadBackroomLots({ silencieux: true });
      alert(`Lot scindé : nouveau lot ${r.new_lot?.lot_number ?? ''} créé.`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la scission');
    } finally {
      setSplitting(false);
    }
  };

  // Tout ce lot en rayon (1 tap) : la quantité transférée est tout le stock réserve du lot.
  const handleMoveWholeLot = (lotId: string) => {
    const pg = products.find(p => p.lots.some(l => l.id === lotId));
    const lot = pg?.lots.find(l => l.id === lotId);
    if (!lot || lot.stock_backroom <= 0) return;
    void handleMoveToDisplay(lotId, lot.stock_backroom);
  };

  /**
   * Annoncer en nouveauté — et RIEN D'AUTRE.
   *
   * Ce bouton rangeait aussi tout le lot, parce qu'il disait « …et l'annoncer ».
   * Résultat : un opérateur qui venait de régler une quantité PARTIELLE et qui
   * tapait « nouveauté » voyait toute sa réserve partir en rayon d'un coup
   * (signalé le 10/09/2026). Le raccourci d'un tap ne valait pas ça : annoncer
   * et ranger sont deux décisions, elles ont deux boutons, et l'annonce ne
   * déplace plus un gramme.
   *
   * Elle reste ici, au rangement, parce que c'est là qu'on SAIT : l'article est
   * dans la main. L'ordre n'a aucune importance — avant ou après le transfert,
   * partiel ou complet.
   */
  const handleAnnoncer = async (lotId: string, kind: 'NEW' | 'BACK' = 'NEW') => {
    const pg = products.find(p => p.lots.some(l => l.id === lotId));
    if (!pg) return;
    try {
      setAnnonceEnCours(lotId);
      await posApi.marquerNouveaute(pg.product.id, kind, vendorId);
      setProduitsAnnonces(prev => new Set(prev).add(pg.product.id));
    } catch (err) {
      alert(err instanceof Error ? err.message : "Échec de l'annonce");
    } finally {
      setAnnonceEnCours(null);
    }
  };

  /**
   * Envoie en rayon TOUS les lots cochés, chacun pour la totalité de sa réserve.
   *
   * Séquentiel à dessein : chaque transfert resynchronise le stock du produit et
   * purge le cache ; les lancer en parallèle ferait se disputer la même fiche.
   * Un échec n'arrête pas les suivants — on dit à la fin ce qui n'est pas passé,
   * plutôt que de laisser la moitié du travail dans le flou.
   */
  const envoyerSelectionEnRayon = async () => {
    const aTraiter = filteredProducts
      .flatMap(pg => pg.lots)
      .filter(l => lotsSelectionnes.has(l.id) && l.stock_backroom > 0);
    if (aTraiter.length === 0) return;

    setEnvoiEnLot({ fait: 0, total: aTraiter.length });
    const echecs: string[] = [];
    for (const [i, lot] of aTraiter.entries()) {
      try {
        await posApi.moveToDisplay(lot.id, lot.stock_backroom);
      } catch (err: any) {
        echecs.push(`${lot.lot_number} (${err?.message || 'échec'})`);
      }
      setEnvoiEnLot({ fait: i + 1, total: aTraiter.length });
    }
    setEnvoiEnLot(null);
    setLotsSelectionnes(new Set());
    await loadBackroomLots({ silencieux: true });
    if (echecs.length > 0) {
      alert(`${aTraiter.length - echecs.length}/${aTraiter.length} lots mis en rayon.\n\nNon passés :\n${echecs.join('\n')}`);
    }
  };

  const openEditModal = async (productId: string) => {
    try {
      const productData = await api.getProduct(productId);
      setSelectedProduct(productData);
      setEditModalOpen(true);
    } catch {
      alert('Erreur lors du chargement du produit');
    }
  };

  const openWebcamForLot = (lotId: string) => {
    setWebcamLotId(lotId);
    setWebcamOpen(true);
  };

  const handleLabelPhotoCapture = async (file: File) => {
    if (!webcamLotId) return;
    try {
      setUploadingPhoto(webcamLotId);
      await posApi.uploadLotLabelPhoto(webcamLotId, file);
      loadBackroomLots({ silencieux: true });
    } catch (err: any) {
      alert(err.message || "Erreur lors de l'upload de la photo");
    } finally {
      setUploadingPhoto(null);
      setWebcamLotId(null);
    }
  };

  const openPrintLabel = (productId: string, lotId: string) => {
    setLabelProductId(productId);
    setLabelLotId(lotId);
    setLabelModalOpen(true);
  };

  const updateQuantity = (lotId: string, value: number) => {
    const lot = products.flatMap(p => p.lots).find(l => l.id === lotId);
    if (!lot) return;
    const newQuantity = Math.max(0, Math.min(value, lot.stock_backroom));
    setMoveQuantities(prev => ({ ...prev, [lotId]: newQuantity }));
    setMoveQuantityInputs(prev => ({ ...prev, [lotId]: String(newQuantity) }));
  };

  const toggleProduct = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) newSet.delete(productId);
      else newSet.add(productId);
      return newSet;
    });
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatPrice = (cents?: number | null) => {
    if (cents === null || cents === undefined) return '-';
    return `${(cents / 100).toFixed(2)} €`;
  };

  const isExpiringSoon = (expiryDate?: string | null) => {
    if (!expiryDate) return false;
    const days = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days <= 7 && days > 0;
  };

  const isExpired = (expiryDate?: string | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const receptionTime = (dateStr?: string | null) =>
    dateStr ? new Date(dateStr).getTime() : 0;

  const maxReceptionTime = (lots: ProductLot[]) =>
    lots.reduce((max, lot) => Math.max(max, receptionTime(lot.reception_date)), 0);

  // Extraire le rayon (1er segment du full_path) d'un produit
  const rayonOf = (pg: BackroomProduct): string | null => {
    const fp = pg.product.category?.full_path;
    if (!fp) return null;
    const first = fp.split(' > ')[0]?.trim();
    return first || null;
  };

  // Liste unique des rayons présents dans les produits du backroom (triés alpha)
  const availableRayons = Array.from(
    new Set(products.map(pg => rayonOf(pg)).filter((r): r is string => !!r))
  ).sort((a, b) => a.localeCompare(b, 'fr'));

  // Liste unique des fournisseurs (extraits des lots) — triés alpha
  const availableSuppliers = Array.from(
    new Set(
      products.flatMap(pg => pg.lots.map(l => l.supplier_name).filter((s): s is string => !!s))
    )
  ).sort((a, b) => a.localeCompare(b, 'fr'));

  const filteredProducts = products
    .filter(pg => {
      // Filtre Rayon
      if (selectedRayon && rayonOf(pg) !== selectedRayon) return false;
      // Filtre Fournisseur (au moins un lot du produit doit matcher)
      if (selectedSupplier && !pg.lots.some(l => l.supplier_name === selectedSupplier)) return false;
      // Filtre recherche texte
      const s = searchTerm.toLowerCase();
      if (!s) return true;
      return (
        pg.product.name.toLowerCase().includes(s) ||
        pg.product.code?.toLowerCase().includes(s) ||
        pg.product.barcode?.toLowerCase().includes(s) ||
        pg.lots.some(lot =>
          lot.lot_number.toLowerCase().includes(s) ||
          lot.supplier_name?.toLowerCase().includes(s) ||
          lot.producer_name?.toLowerCase().includes(s)
        )
      );
    })
    .map(pg => ({
      ...pg,
      lots: [...pg.lots].sort((a, b) => receptionTime(b.reception_date) - receptionTime(a.reception_date)),
    }))
    .sort((a, b) => maxReceptionTime(b.lots) - maxReceptionTime(a.lots));

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-lg text-gray-500">Chargement des lots en réserve...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Ce qui est arrivé en rayon cette semaine, et ce qui a été annoncé.
          Informatif : aucun bouton, aucune alerte. */}
      {decompteSemaine && decompteSemaine.total > 0 && (
        <p className="mb-3 text-xs text-gray-500">
          {decompteSemaine.total} première{decompteSemaine.total > 1 ? 's' : ''} mise
          {decompteSemaine.total > 1 ? 's' : ''} en rayon cette semaine ·{' '}
          {decompteSemaine.annonces} annoncée{decompteSemaine.annonces > 1 ? 's' : ''} en nouveauté
        </p>
      )}

      {/* Recherche */}
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Rechercher par nom, code, code-barres, lot, fournisseur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
        />
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="px-4 flex items-center justify-center bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 min-h-[48px]"
          aria-label="Scanner via caméra"
          title="Scanner via caméra"
        >
          <Camera className="w-5 h-5" />
        </button>
      </div>

      {/* Filtres Rayon */}
      {availableRayons.length > 0 && (
        <div className="mb-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-1">Rayon</div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
            <button
              onClick={() => setSelectedRayon(null)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold min-h-[48px] transition-colors ${
                selectedRayon === null
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous ({products.length})
            </button>
            {availableRayons.map(rayon => {
              const count = products.filter(pg => rayonOf(pg) === rayon).length;
              return (
                <button
                  key={rayon}
                  onClick={() => setSelectedRayon(rayon === selectedRayon ? null : rayon)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold min-h-[48px] transition-colors whitespace-nowrap ${
                    selectedRayon === rayon
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {rayon} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtres Fournisseur */}
      {availableSuppliers.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-1">Fournisseur</div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
            <button
              onClick={() => setSelectedSupplier(null)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold min-h-[48px] transition-colors ${
                selectedSupplier === null
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous
            </button>
            {availableSuppliers.map(supplier => {
              const count = products.filter(pg => pg.lots.some(l => l.supplier_name === supplier)).length;
              return (
                <button
                  key={supplier}
                  onClick={() => setSelectedSupplier(supplier === selectedSupplier ? null : supplier)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold min-h-[48px] transition-colors whitespace-nowrap ${
                    selectedSupplier === supplier
                      ? 'bg-orange-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {supplier} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Produits</p>
          <p className="text-xl font-bold text-blue-900">{products.length}</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Lots</p>
          <p className="text-xl font-bold text-green-900">
            {products.reduce((sum, p) => sum + p.lots.length, 0)}
          </p>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Nouveaux</p>
          <p className="text-xl font-bold text-purple-900">
            {products.filter(p => !p.product.active).length}
          </p>
        </div>
      </div>

      {/* Liste des produits */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Aucun produit en réserve</p>
          <p className="text-gray-500 text-sm mt-1">
            Les produits apparaîtront après application d'une livraison
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Barre d'action de la sélection : collante, pour rester sous la main
              quand on descend la liste des lots. */}
          {(lotsSelectionnes.size > 0 || envoiEnLot) && (
            <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 rounded-xl border-2 border-green-300 bg-green-50/95 px-4 py-3 shadow-md backdrop-blur">
              <span className="text-base font-bold text-green-900">
                {envoiEnLot
                  ? `Mise en rayon ${envoiEnLot.fait}/${envoiEnLot.total}…`
                  : `${lotsSelectionnes.size} lot${lotsSelectionnes.size > 1 ? 's' : ''} sélectionné${lotsSelectionnes.size > 1 ? 's' : ''}`}
              </span>
              <button
                type="button"
                onClick={envoyerSelectionEnRayon}
                disabled={!!envoiEnLot}
                className="min-h-[48px] rounded-lg bg-green-600 px-5 py-3 text-base font-bold text-white active:bg-green-800 disabled:bg-gray-300"
              >
                ⬆️ Tout mettre en rayon
              </button>
              <button
                type="button"
                onClick={() => setLotsSelectionnes(new Set())}
                disabled={!!envoiEnLot}
                className="min-h-[48px] rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-base font-semibold text-gray-700 active:bg-gray-100 disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          )}
          {filteredProducts.map((pg) => {
            const isExpanded = expandedProducts.has(pg.product.id);
            const isHighlighted = highlightedProductId === pg.product.id;

            return (
              <div
                key={pg.product.id}
                ref={(el) => { productRefs.current[pg.product.id] = el; }}
                className={`bg-white border rounded-lg overflow-hidden shadow-sm transition-all ${
                  isHighlighted
                    ? 'border-blue-500 ring-2 ring-blue-300 animate-pulse'
                    : 'border-gray-200'
                }`}
              >
                {/* En-tête produit */}
                <div
                  onClick={() => toggleProduct(pg.product.id)}
                  className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    {/* Sélection : à gauche du bloc, là où l'œil passe en descendant
                        la liste. Cocher ici prend TOUS les lots en réserve du produit
                        (il n'y en a qu'un dans la plupart des cas). */}
                    {pg.total_backroom > 0 && (
                      <label
                        className="flex shrink-0 cursor-pointer items-center self-stretch pr-1"
                        onClick={(e) => e.stopPropagation()}
                        title="Sélectionner ce produit pour une mise en rayon groupée"
                      >
                        <input
                          type="checkbox"
                          checked={pg.lots.filter(l => l.stock_backroom > 0).every(l => lotsSelectionnes.has(l.id))}
                          onChange={() => basculerSelectionProduit(pg.lots)}
                          className="h-7 w-7 rounded border-2 border-gray-400 text-green-600 focus:ring-green-500"
                        />
                      </label>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-base truncate">{pg.product.name}</h3>
                        {!pg.product.active && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full whitespace-nowrap">
                            ⭐ Nouveau
                          </span>
                        )}
                        {pg.lots.some(lot => isExpiringSoon(lot.expiry_date)) && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full flex items-center gap-1 whitespace-nowrap">
                            <AlertCircle className="w-3 h-3" />
                            DLC proche
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4 text-xs text-gray-500">
                        {pg.product.code && <span>Code: {pg.product.code}</span>}
                        {pg.product.barcode && <span>EAN: {pg.product.barcode}</span>}
                      </div>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <div className="text-xs text-gray-500">{pg.lots.length} lot(s)</div>
                      <div className="text-lg font-bold text-blue-600">
                        {pg.total_backroom.toFixed(pg.total_backroom % 1 === 0 ? 0 : 2)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {isExpanded ? '▲' : '▼'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lots détaillés */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-3">
                    {pg.lots.map((lot) => (
                      <div
                        key={lot.id}
                        className={`bg-white border rounded-lg p-3 ${
                          isExpired(lot.expiry_date) ? 'border-red-300 bg-red-50'
                            : isExpiringSoon(lot.expiry_date) ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex gap-3">
                          {/* Colonne de sélection, à gauche du bloc entier : la case
                              se vise sans chercher, et toute la colonne est cliquable. */}
                          {lot.stock_backroom > 0 && (
                            <label
                              className="flex shrink-0 cursor-pointer items-start pt-0.5"
                              onClick={(e) => e.stopPropagation()}
                              title="Sélectionner ce lot pour une mise en rayon groupée"
                            >
                              <input
                                type="checkbox"
                                checked={lotsSelectionnes.has(lot.id)}
                                onChange={() => basculerSelection(lot.id)}
                                className="h-7 w-7 rounded border-2 border-gray-400 text-green-600 focus:ring-green-500"
                              />
                            </label>
                          )}
                          <div className="grid flex-1 grid-cols-1 lg:grid-cols-2 gap-3">
                          {/* Info lot */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm">Lot {lot.lot_number}</span>
                              {lot.supplier_lot_number && (
                                <span className="text-xs text-gray-500">({lot.supplier_lot_number})</span>
                              )}
                              {isExpired(lot.expiry_date) && (
                                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">Expiré</span>
                              )}
                              <button
                                onClick={() => openSplit(lot)}
                                className="ml-auto px-2 py-1 text-xs font-medium rounded border border-indigo-300 bg-indigo-50 text-indigo-700 active:bg-indigo-100 min-h-[48px]"
                                title="Ce lot contient des colis avec deux DLC différentes ? Créez le second lot."
                              >
                                ✂️ Scinder
                              </button>
                            </div>

                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                              <div className="flex items-center gap-1.5 text-gray-600">
                                <Calendar className="w-3.5 h-3.5" />
                                Réception: {formatDate(lot.reception_date)}
                              </div>
                              {lot.expiry_date && (
                                <div className={`flex items-center gap-1.5 ${
                                  isExpired(lot.expiry_date) ? 'text-red-600 font-semibold'
                                    : isExpiringSoon(lot.expiry_date) ? 'text-orange-600 font-semibold'
                                    : 'text-gray-600'
                                }`}>
                                  <Calendar className="w-3.5 h-3.5" />
                                  DLC: {formatDate(lot.expiry_date)}
                                </div>
                              )}
                              {lot.supplier_name && (
                                <div className="flex items-center gap-1.5 text-gray-600">
                                  <Factory className="w-3.5 h-3.5" />
                                  {lot.supplier_name}
                                </div>
                              )}
                              {lot.producer_name && (
                                <div className="flex items-center gap-1.5 text-gray-600">
                                  <User className="w-3.5 h-3.5" />
                                  {lot.producer_name}
                                </div>
                              )}
                              {lot.organic_certifier && (
                                <div className="flex items-center gap-1.5 text-green-700">
                                  <BadgeCheck className="w-3.5 h-3.5" />
                                  {lot.organic_certifier}
                                </div>
                              )}
                            </div>

                            <div className="flex gap-4 text-xs pt-1 border-t flex-wrap">
                              {lot.purchase_price_cents != null && (
                                <div>
                                  <span className="text-gray-500">Achat: </span>
                                  <span className="font-semibold">{formatPrice(lot.purchase_price_cents)}</span>
                                </div>
                              )}
                              {/* Prix de vente = prix CAISSE courant (source de vérité POS),
                                  et non le prix figé du lot qui a pu dériver depuis la réception. */}
                              {(pg.product as any).current_price_ttc_cents != null && (
                                <div className="px-2 py-0.5 rounded bg-blue-50 border border-blue-200">
                                  <span className="text-blue-700">Vente TTC: </span>
                                  <span className="font-bold text-blue-900">{formatPrice((pg.product as any).current_price_ttc_cents)}</span>
                                </div>
                              )}
                              {lot.unit_price_ttc_cents != null &&
                                lot.unit_price_ttc_cents !== (pg.product as any).current_price_ttc_cents && (
                                <div
                                  className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200"
                                  title="Prix saisi sur ce lot à la réception. Il n'est PAS imprimé : l'étiquette rayon affiche toujours le prix de vente courant, celui que la caisse facture. Repère historique uniquement."
                                >
                                  <span className="text-amber-700">Prix à la réception: </span>
                                  <span className="font-semibold text-amber-900">{formatPrice(lot.unit_price_ttc_cents)}</span>
                                </div>
                              )}
                              {lot.suggested_price_cents != null && lot.suggested_price_cents !== lot.unit_price_ttc_cents && (
                                <div>
                                  <span className="text-gray-500">Conseillé: </span>
                                  <span className="font-semibold text-green-700">{formatPrice(lot.suggested_price_cents)}</span>
                                </div>
                              )}
                            </div>

                            {/* Traçabilité + Actions */}
                            <div className="flex items-center gap-1.5 pt-1 border-t flex-wrap">
                              {lot.source_document_url && (
                                <a
                                  href={lot.source_document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 px-2 py-1.5 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md min-h-[48px]"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  Document
                                </a>
                              )}
                              {lot.label_photo_url && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setPreviewPhotoUrl(lot.label_photo_url!); }}
                                  className="flex items-center gap-1 px-2 py-1.5 text-xs text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md min-h-[48px]"
                                >
                                  <Image className="w-3.5 h-3.5" />
                                  Étiquette
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); openWebcamForLot(lot.id); }}
                                disabled={uploadingPhoto === lot.id}
                                className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md min-h-[48px] disabled:opacity-50"
                              >
                                <Camera className="w-3.5 h-3.5" />
                                {uploadingPhoto === lot.id ? 'Upload...' : lot.label_photo_url ? 'Remplacer' : 'Photo'}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditModal(pg.product.id); }}
                                className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md min-h-[48px]"
                              >
                                <Edit className="w-3.5 h-3.5" />
                                Produit
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openPrintLabel(pg.product.id, lot.id); }}
                                className="flex items-center gap-1 px-2 py-1.5 text-xs text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md min-h-[48px]"
                              >
                                <Printer className="w-3.5 h-3.5" />
                                Étiquette prix
                              </button>
                            </div>
                          </div>

                          {/* Contrôles transfert */}
                          <div className="flex flex-col justify-between">
                            <div className="mb-2">
                              <div className="text-xs text-gray-500">Stock réserve</div>
                              <div className="text-xl font-bold text-blue-600">
                                {formatQty(lot.stock_backroom)} {lot.unit || pg.product.unit || 'unités'}
                              </div>
                              {lot.stock_display > 0 && (
                                <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-900 text-xs font-semibold">
                                  📦 {formatQty(lot.stock_display)} déjà en rayon (ce lot)
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              {/* Action principale : tout ce lot en rayon en 1 tap */}
                              <button
                                onClick={() => handleMoveWholeLot(lot.id)}
                                disabled={movingLot === lot.id || lot.stock_backroom <= 0}
                                className="w-full py-3 px-4 rounded-lg font-semibold transition-colors min-h-[48px] bg-green-600 hover:bg-green-700 text-white active:bg-green-800 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                              >
                                {movingLot === lot.id
                                  ? 'Transfert...'
                                  : `⬆️ Tout ce lot en rayon (${formatQty(lot.stock_backroom)} ${lot.unit || pg.product.unit || 'u'})`}
                              </button>

                              {/* Annoncer : un geste À PART, qui ne déplace AUCUN
                                  stock. Combiné au rangement, il envoyait tout le
                                  lot en rayon alors qu'on venait de régler une
                                  quantité partielle. Rien n'est présélectionné :
                                  ne pas annoncer reste le défaut — une nouveauté
                                  oubliée ne coûte qu'une occasion, une fausse
                                  annonce coûte la confiance. */}
                              {produitsAnnonces.has(pg.product.id) ? (
                                <div className="mt-2 w-full py-2 px-4 rounded-lg text-sm font-semibold text-emerald-800 bg-emerald-50 border border-emerald-300 text-center">
                                  ✨ Annoncé en nouveauté
                                </div>
                              ) : (
                                <div className="mt-2 flex gap-2">
                                  <button
                                    onClick={() => void handleAnnoncer(lot.id, 'NEW')}
                                    disabled={annonceEnCours === lot.id}
                                    className="flex-1 py-3 px-3 rounded-lg font-semibold transition-colors min-h-[48px] border-2 border-emerald-600 text-emerald-800 hover:bg-emerald-50 active:bg-emerald-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed"
                                  >
                                    {annonceEnCours === lot.id ? 'Annonce...' : '✨ Annoncer : Nouveau'}
                                  </button>
                                  <button
                                    onClick={() => void handleAnnoncer(lot.id, 'BACK')}
                                    disabled={annonceEnCours === lot.id}
                                    className="py-3 px-3 rounded-lg font-medium transition-colors min-h-[48px] border border-emerald-400 text-emerald-700 hover:bg-emerald-50 disabled:border-gray-300 disabled:text-gray-400"
                                  >
                                    De retour
                                  </button>
                                </div>
                              )}

                              {/* Partiel (optionnel) : choisir une quantité précise */}
                              {(() => {
                                const isWeight = !!(
                                  (lot as any).is_weighted ||
                                  (pg.product as any).is_weighted ||
                                  lot.unit === 'kg' ||
                                  pg.product.unit === 'kg'
                                );
                                return (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateQuantity(lot.id, (moveQuantities[lot.id] || 0) - 1)}
                                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-bold min-h-[48px] min-w-[48px]"
                                  disabled={movingLot === lot.id}
                                >
                                  -
                                </button>
                                <input
                                  type={isWeight ? 'text' : 'number'}
                                  inputMode={isWeight ? 'decimal' : 'numeric'}
                                  value={moveQuantityInputs[lot.id] ?? String(moveQuantities[lot.id] || 0)}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setMoveQuantityInputs(prev => ({ ...prev, [lot.id]: raw }));
                                    const n = parseFloat(raw.replace(',', '.'));
                                    const capped = isNaN(n) ? 0 : Math.max(0, Math.min(n, lot.stock_backroom));
                                    setMoveQuantities(prev => ({ ...prev, [lot.id]: capped }));
                                  }}
                                  onBlur={() => setMoveQuantityInputs(prev => ({ ...prev, [lot.id]: String(moveQuantities[lot.id] ?? 0) }))}
                                  onFocus={(e) => e.target.select()}
                                  className={`flex-1 px-3 py-2 border rounded-lg text-center min-h-[48px] font-semibold ${
                                    (moveQuantities[lot.id] || 0) > 0
                                      ? 'border-green-400 bg-green-50 text-green-900'
                                      : 'border-gray-300 text-gray-500'
                                  }`}
                                  min="0"
                                  max={lot.stock_backroom}
                                  step={isWeight ? '0.01' : '1'}
                                  disabled={movingLot === lot.id}
                                />
                                <button
                                  onClick={() => updateQuantity(lot.id, (moveQuantities[lot.id] || 0) + 1)}
                                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-bold min-h-[48px] min-w-[48px]"
                                  disabled={movingLot === lot.id}
                                >
                                  +
                                </button>
                              </div>
                                );
                              })()}
                              <button
                                onClick={() => handleMoveToDisplay(lot.id, moveQuantities[lot.id] || 0)}
                                disabled={movingLot === lot.id || (moveQuantities[lot.id] || 0) <= 0}
                                className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm border transition-colors min-h-[48px] ${
                                  movingLot === lot.id || (moveQuantities[lot.id] || 0) <= 0
                                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'border-green-600 text-green-700 hover:bg-green-50'
                                }`}
                              >
                                {movingLot === lot.id
                                  ? 'Transfert...'
                                  : (moveQuantities[lot.id] || 0) > 0
                                    ? `Mettre seulement ${formatQty(moveQuantities[lot.id])} ${lot.unit || pg.product.unit || 'u'} en rayon`
                                    : 'Mettre seulement la quantité choisie'}
                              </button>
                            </div>
                          </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <ProductEditModal
        product={selectedProduct}
        isOpen={editModalOpen}
        onClose={() => { setEditModalOpen(false); setSelectedProduct(null); }}
        onSave={() => loadBackroomLots({ silencieux: true })}
      />

      <WebcamCaptureModal
        isOpen={webcamOpen}
        onClose={() => { setWebcamOpen(false); setWebcamLotId(null); }}
        onCapture={handleLabelPhotoCapture}
      />

      {labelModalOpen && labelProductId && (
        <PrintShelfLabelsModal
          onClose={() => { setLabelModalOpen(false); setLabelProductId(null); setLabelLotId(null); }}
          preselectedProductId={labelProductId}
          preselectedLotId={labelLotId || undefined}
        />
      )}

      {/* Preview photo */}
      {previewPhotoUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewPhotoUrl(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewPhotoUrl(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-sm"
            >
              Fermer
            </button>
            <img src={previewPhotoUrl} alt="Photo étiquette" className="w-full rounded-lg shadow-2xl" />
          </div>
        </div>
      )}

      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(code) => {
          setSearchTerm(code);
          const match = products.find(pg =>
            pg.product.barcode === code || pg.product.code === code
          );
          if (match) {
            setExpandedProducts(prev => new Set(prev).add(match.product.id));
            setHighlightedProductId(match.product.id);
            setTimeout(() => {
              productRefs.current[match.product.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
            setTimeout(() => setHighlightedProductId(null), 3000);
          }
        }}
        title="Scanner un produit"
      />

      {/* Scission de lot : deux DLC dans une même livraison */}
      {splitLot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="text-lg font-bold text-gray-900">Scinder le lot {splitLot.lot_number}</h3>
            <p className="mt-1 text-sm text-gray-600">
              À utiliser quand les colis reçus portent <strong>deux DLC différentes</strong>.
              Indiquez la quantité qui porte l'autre date : elle passera sur un nouveau lot,
              qui garde le même fournisseur, le même prix et la même traçabilité.
            </p>

            <div className="mt-4 text-xs text-gray-500">
              Disponible — réserve : <strong>{formatQty(splitLot.stock_backroom ?? 0)}</strong>
              {' · '}rayon : <strong>{formatQty(splitLot.stock_display ?? 0)}</strong>
              {splitLot.expiry_date && <> · DLC actuelle : <strong>{formatDate(splitLot.expiry_date)}</strong></>}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quantité en réserve</label>
                <input
                  type="text" inputMode="decimal" value={splitQtyBackroom}
                  onChange={(e) => setSplitQtyBackroom(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quantité en rayon</label>
                <input
                  type="text" inputMode="decimal" value={splitQtyDisplay}
                  onChange={(e) => setSplitQtyDisplay(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">DLC du nouveau lot</label>
              <DLCInput value={splitDlc} onChange={(iso) => setSplitDlc(iso)} className="w-full" />
            </div>

            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setSplitLot(null)}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 text-gray-700 font-medium active:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={confirmSplit}
                disabled={splitting}
                className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold active:bg-indigo-700 disabled:opacity-50"
              >
                {splitting ? 'Scission…' : 'Créer le second lot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
